import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
//Helpers
import { ChunkRequest } from 'src/services/gen_2_http_reqs';
import TemplateDeviceSettings from 'src/components/XT1/TemplateDeviceSettings.svelte';
import TemplateDeviceSettingItem from 'src/components/XT1/TemplateDeviceSettingItem.svelte';
//Types
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { VirtualComponentTypes, VirtualComponentUI } from 'src/types/VirtualComponents';

export const getXT1DeviceSettings = (device: Device) => {
	switch (device.category) {
		case 'cover':
		case 'module':
			return TemplateDeviceSettings;
	}
	return null;
};

export type XT1DeviceSettings = {
	key: string;
	type?: VirtualComponentTypes | 'number-range';
	view: VirtualComponentUI['view'] | 'input';
	options: string[];
	title: string;
	desc?: string | { title: string }[];
	range?: {
		min: number;
		max: number;
	};
};

const XT1_DEVICE_SETTINGS: Record<Device['id'], XT1DeviceSettings[]> = {};

export const fetchXT1DeviceSettings = async (device: Device) => {
	if (!XT1_DEVICE_SETTINGS[device.id]) {
		const chunkRequest = new ChunkRequest(device, 'Service.ListConfigOptions', {
			id: 0,
		});
		try {
			const res: SettingRequest = await chunkRequest.get();
			if (res?.isok) {
				let config: XT1DeviceSettings[] = (res?.['props'] || res?.data?.props || []).map(
					(i: XT1DeviceSettings) => {
						return {
							...i,
							desc: getAdditionalDesc(i.key),
						};
					},
				);
				XT1_DEVICE_SETTINGS[device.id] = config;
			}
		} catch (error) {
			console.warn(error);
		}
	}
	return XT1_DEVICE_SETTINGS[device.id] || [];
};

export const getXT1SettingsNavigationItems = (device: Device, settings: XT1DeviceSettings[]) => {
	return settings
		.filter((setting) => setting.key)
		.map((setting) => ({
			id: `xt1_${setting.key}`,
			parentID: 'settings',
			title: setting.title || setting.key,
			hideAvatar: true,
			active: false,
			disabled: false,
			component: {
				component: TemplateDeviceSettingItem,
				titleDefault: setting.title || setting.key,
				props: { device, setting },
			},
		}));
};

const getAdditionalDesc = (key: string) => {
	const $format = get(format);
	switch (key) {
		case 'ext_button':
			return [
				{
					title: $format('label_ext_button_dual_button_reverse_mode', {
						default:
							'Dual button reverse mode: Press one button to open the curtain and the other to close it. If the same button is pressed again during movement, the curtain stops. Pressing the opposite button reverses the direction of movement.',
					}),
				},
				{
					title: $format('label_ext_button_dual_button_mode', {
						default:
							'Dual button mode: Press one button to open the curtain. Press either button again to stop it.',
					}),
				},
				{
					title: $format('label_ext_button_rocket_switch_mode', {
						default:
							'Rocker switch: Press and hold one end of the switch to open the curtain, or the other end to close it. Releasing the switch will stop the movement.',
					}),
				},
				{
					title: $format('label_ext_button_triple_button_mode', {
						default:
							'Triple-button mode: Use one button to open, one to stop, and one to close the curtain.',
					}),
				},
			];
	}
};
